import { useNavigate } from '@tanstack/react-router';
import { T } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import { CircleNotch as CircleNotchIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useDiscountUsageV2 } from '@/api/rpc/hooks/accounts-receivable/useDiscountGroupsV2';

/**
 * Sales orders a discount group (or a single element) was applied to. Each row
 * links to the sales-order-v3 review page via its originating draft; orders
 * with no draft link (rare — non-OMS-v3 origin) render un-clickable.
 */
export function UsageTable({
  discountGroupId,
  discountElementId,
  className,
}: {
  discountGroupId?: string;
  discountElementId?: string;
  className?: string;
}) {
  const navigate = useNavigate();
  const { data, isLoading } = useDiscountUsageV2({
    discountGroupId,
    discountElementId,
  });
  const usage = data?.usage ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <CircleNotchIcon className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (usage.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        <T context="discount usage empty state">
          Not applied to any sales orders yet.
        </T>
      </p>
    );
  }

  return (
    <div className={cn('overflow-x-auto rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T context="discount usage column">Order</T>
            </TableHead>
            <TableHead>
              <T context="discount usage column">Customer</T>
            </TableHead>
            <TableHead>
              <T context="discount usage column">Date</T>
            </TableHead>
            <TableHead className="text-right">
              <T context="discount usage column">Total</T>
            </TableHead>
            <TableHead className="text-right">
              <T context="discount usage column, number of discounted lines">
                Lines
              </T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usage.map((row) => {
            const linkable = !!row.draftSalesOrderId;
            return (
              <TableRow
                key={row.salesOrderId}
                className={cn(linkable && 'cursor-pointer hover:bg-muted/50')}
                onClick={
                  linkable
                    ? () =>
                        navigate({
                          to: '/sales-orders/$draftId',
                          params: { draftId: row.draftSalesOrderId as string },
                        })
                    : undefined
                }
              >
                <TableCell className="font-medium">
                  {row.customerPurchaseOrderNumber ?? `#${row.internalId}`}
                </TableCell>
                <TableCell>{row.customerName ?? '—'}</TableCell>
                <TableCell className="whitespace-nowrap">
                  {row.orderDate ?? '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.totalAmount != null
                    ? formatCurrency(Number(row.totalAmount))
                    : '—'}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.lineCount}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
